/**
 * Site-wide toast manager. Call `showToast` / `showCartToast` from any module
 * (React components, OrbieeFlow, contexts). Mount `<ToastProvider />` in App.
 */

const MAX_TOASTS = 3;
const DEFAULT_DURATION_MS = 4000;

/**
 * Keep at most MAX_TOASTS, newest first. A pinned toast (`duration: 0`) is
 * never pushed out by transient ones: it is up because something needs the
 * shopper's hand (the session takeover), and three quick cart toasts must
 * not bury it. Transient toasts are dropped oldest-first to make room; if
 * the pinned ones alone exceed the cap, that is on the callers.
 */
function trimToasts(list) {
  if (list.length <= MAX_TOASTS) return list;
  const pinned = list.filter((t) => t.duration === 0);
  const room = Math.max(0, MAX_TOASTS - pinned.length);
  const transient = list.filter((t) => t.duration > 0).slice(0, room);
  const keep = new Set([...pinned, ...transient]);
  return list.filter((t) => keep.has(t));
}

/**
 * The toast's one button. `{ label, onClick }` → kept as is; anything
 * without a label is no action. ToastProvider runs `onClick` and then
 * dismisses the toast — a toast that needs to stay after its action
 * re-shows itself.
 */
function normalizeAction(input) {
  if (!input || typeof input !== "object") return null;
  const label = typeof input.label === "string" ? input.label.trim() : "";
  if (!label) return null;
  return {
    label,
    onClick: typeof input.onClick === "function" ? input.onClick : null,
  };
}

/** @typedef {"success" | "error" | "warning" | "info"} ToastSeverity */
/** @typedef {"add" | "remove" | "update" | "clear"} CartToastOperation */

const CART_COPY = {
  add: { title: "Added to cart", severity: "success" },
  remove: { title: "Removed from cart", severity: "success" },
  update: { title: "Cart updated", severity: "success" },
  clear: { title: "Cart cleared", severity: "success" },
};

/** @type {object[]} */
let toasts = [];
/** @type {Array<(next: object[]) => void>} */
let subscribers = [];
/** @type {Map<string, ReturnType<typeof setTimeout>>} */
const timers = new Map();

function notify() {
  const snapshot = toasts.slice();
  subscribers.forEach((cb) => {
    try {
      cb(snapshot);
    } catch (err) {
      console.warn("[toast] subscriber threw", err);
    }
  });
}

function nextId() {
  return `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function normalizeProduct(input) {
  if (!input || typeof input !== "object") return null;
  const imageCandidate =
    (typeof input.image === "string" && input.image) ||
    (typeof input.image_uri === "string" && input.image_uri) ||
    (Array.isArray(input.images) &&
      typeof input.images[0] === "string" &&
      input.images[0]) ||
    (Array.isArray(input.images) &&
      input.images[0] &&
      typeof input.images[0].url === "string" &&
      input.images[0].url) ||
    "";
  const name =
    (typeof input.productName === "string" && input.productName) ||
    (typeof input.title === "string" && input.title) ||
    (typeof input.name === "string" && input.name) ||
    (typeof input.product_title === "string" && input.product_title) ||
    "";
  const qtyRaw = input.quantity ?? input.qty;
  const quantity =
    typeof qtyRaw === "number" && Number.isFinite(qtyRaw) && qtyRaw > 0
      ? Math.floor(qtyRaw)
      : null;
  let size = input.size ?? input.selected_size ?? null;
  if (size == null && Array.isArray(input.attributes?.size) && input.attributes.size[0] != null) {
    size = input.attributes.size[0];
  }
  size = size != null && String(size).trim() !== "" ? String(size) : null;

  if (!imageCandidate && !name) return null;
  return {
    image: imageCandidate || null,
    productName: name || null,
    quantity,
    size,
  };
}

/**
 * Subscribe to toast list changes. Returns unsubscribe.
 * @param {(next: object[]) => void} callback
 */
export function subscribeToasts(callback) {
  subscribers.push(callback);
  callback(toasts.slice());
  return () => {
    subscribers = subscribers.filter((cb) => cb !== callback);
  };
}

export function getToasts() {
  return toasts.slice();
}

/**
 * Dismiss one toast (or all if id omitted).
 * @param {string} [id]
 */
export function hideToast(id) {
  if (id == null) {
    timers.forEach((t) => clearTimeout(t));
    timers.clear();
    toasts = [];
    notify();
    return;
  }
  const timer = timers.get(id);
  if (timer) {
    clearTimeout(timer);
    timers.delete(id);
  }
  const next = toasts.filter((t) => t.id !== id);
  if (next.length === toasts.length) return;
  toasts = next;
  notify();
}

/**
 * Show a toast. Any page or service can call this.
 *
 * String form: `showToast("Saved")`
 * Object form: `{ title, message, severity, image, productName, quantity, size, products, duration, id, action }`
 *
 * `duration: 0` pins the toast: no timer, and it survives the three-toast
 * cap. It leaves on its close button, on its action, or on `hideToast(id)`.
 * `action: { label, onClick }` renders one button; pressing it runs
 * `onClick` and dismisses the toast.
 *
 * @param {string | {
 *   title?: string,
 *   message?: string,
 *   severity?: ToastSeverity,
 *   image?: string | null,
 *   productName?: string | null,
 *   quantity?: number | null,
 *   size?: string | null,
 *   products?: object[],
 *   duration?: number,
 *   id?: string,
 *   action?: { label: string, onClick?: () => void } | null,
 * }} options
 * @returns {string} toast id
 */
export function showToast(options) {
  const opts =
    typeof options === "string" ? { title: options } : options && typeof options === "object" ? options : {};

  const productsIn = Array.isArray(opts.products)
    ? opts.products.map(normalizeProduct).filter(Boolean)
    : [];
  const primaryFromFields = normalizeProduct(opts);
  if (primaryFromFields && productsIn.length === 0) {
    productsIn.push(primaryFromFields);
  }

  const primary = productsIn[0] || null;
  const extraCount = Math.max(0, productsIn.length - 1);
  const extraMessage =
    extraCount > 0
      ? `and ${extraCount} more item${extraCount === 1 ? "" : "s"}`
      : "";

  const toast = {
    id: typeof opts.id === "string" && opts.id ? opts.id : nextId(),
    title: typeof opts.title === "string" && opts.title ? opts.title : opts.message || "",
    message:
      typeof opts.message === "string" && opts.message
        ? opts.message
        : extraMessage,
    severity: opts.severity === "error" || opts.severity === "warning" || opts.severity === "info"
      ? opts.severity
      : "success",
    image: primary?.image || null,
    productName: primary?.productName || null,
    quantity: primary?.quantity ?? null,
    size: primary?.size || null,
    products: productsIn,
    duration:
      typeof opts.duration === "number" && Number.isFinite(opts.duration)
        ? Math.max(0, opts.duration)
        : DEFAULT_DURATION_MS,
    action: normalizeAction(opts.action),
    createdAt: Date.now(),
  };

  if (!toast.title && !toast.productName && !toast.message) {
    return toast.id;
  }

  const existingTimer = timers.get(toast.id);
  if (existingTimer) {
    clearTimeout(existingTimer);
    timers.delete(toast.id);
  }

  toasts = trimToasts([toast, ...toasts.filter((t) => t.id !== toast.id)]);
  notify();

  if (toast.duration > 0) {
    const timer = setTimeout(() => hideToast(toast.id), toast.duration);
    timers.set(toast.id, timer);
  }

  return toast.id;
}

/**
 * Cart-specific helper used by OrbieeFlow and CartContext.
 * `product` may be one item, an array, or omitted (clear cart).
 *
 * @param {CartToastOperation} operation
 * @param {object | object[] | null} [product]
 * @param {{ duration?: number, id?: string, message?: string }} [extra]
 * @returns {string | null}
 */
export function showCartToast(operation, product, extra = {}) {
  const copy = CART_COPY[operation];
  if (!copy) return null;

  const list = Array.isArray(product) ? product : product ? [product] : [];
  return showToast({
    title: copy.title,
    severity: copy.severity,
    products: list,
    message: extra.message,
    duration: extra.duration,
    id: extra.id || `cart-${operation}`,
  });
}
